import { Text } from "#components/Text";
import { Box, Flex, Grid, useColorModeValue } from "@chakra-ui/react";
import { ReactNode } from "react";

export function Tag({ children }: { children: ReactNode }) {
  const name = children?.toString();
  const background = useColorModeValue(
    name === "N2-B"
      ? "purple.100"
      : name === "N2-AB"
        ? "teal.100"
        : name === "F"
          ? "pink.100"
          : "gray.100",
    name === "N2-B"
      ? "purple.900"
      : name === "N2-AB"
        ? "teal.900"
        : name === "F"
          ? "pink.900"
          : "gray.900",
  );

  return (
    <Box
      as="span"
      background={background}
      display="inline-block"
      fontFamily="sans"
      paddingX="1"
      lineHeight="inherit"
      rounded="md"
      textAlign="center"
    >
      {children}
    </Box>
  );
}

function Display1Name({
  label,
  prefix,
  suffix,
}: {
  label: string;
  prefix: string;
  suffix: string;
}) {
  const suffixColors = useColorModeValue(
    [
      "red.700",
      ...Array(8)
        .fill(null)
        .flatMap((_, i) => Array(2).fill(`gray.${(9 - i) * 100}`)),
    ],
    [
      "red.300",
      ...Array(8)
        .fill(null)
        .flatMap((_, i) => Array(2).fill(`whiteAlpha.${(9 - i) * 100}`)),
    ],
  );

  const suffixChars = suffix.split("").map((char, i) => {
    const color = suffixColors[i];
    if (color) {
      return (
        <Text
          key={i}
          as="span"
          color={color}
          fontWeight={i === 0 ? "bold" : ""}
        >
          {char}
        </Text>
      );
    }
  });

  return (
    <>
      <Tag>{label}</Tag>
      <Text as="pre">
        {prefix}
        {suffixChars}
      </Text>
    </>
  );
}

export function Display1() {
  const sharedPrefix = `methionyl threonyl threonyl glutaminyl a`.replaceAll(
    " ",
    "",
  );
  const n2bSuffix = `lanyl prolyl threonyl phenylalanyl`.replaceAll(" ", "");
  const fSuffix = `rginyl tyrosyl glutamyl seryl`.replaceAll(" ", "");

  return (
    <Grid
      templateColumns="max-content 1fr"
      columnGap="2"
      rowGap="1"
      minWidth="45ch"
      maxWidth="full"
      overflow="hidden"
      alignItems="baseline"
    >
      <Display1Name label="N2-B" prefix={sharedPrefix} suffix={n2bSuffix} />
      <Display1Name label="F" prefix={sharedPrefix} suffix={fSuffix} />
    </Grid>
  );
}

const AMINO_ACID_NAME = {
  A: "alanine",
  R: "arginine",
  N: "asparagine",
  D: "aspartic acid",
  C: "cysteine",
  Q: "glutamine",
  E: "glutamic acid",
  G: "glycine",
  H: "histidine",
  I: "isoleucine",
  L: "leucine",
  K: "lysine",
  M: "methionine",
  F: "phenylalanine",
  P: "proline",
  S: "serine",
  T: "threonine",
  W: "tryptophan",
  Y: "tyrosine",
  V: "valine",
};

const ACYL_NAME = {
  A: "alanyl",
  R: "arginyl",
  N: "asparaginyl",
  D: "aspartyl",
  C: "cysteinyl",
  Q: "glutaminyl",
  E: "glutamyl",
  G: "glycyl",
  H: "histidyl",
  I: "isoleucyl",
  L: "leucyl",
  K: "lysyl",
  M: "methionyl",
  F: "phenylalanyl",
  P: "prolyl",
  S: "seryl",
  T: "threonyl",
  W: "tryptophyl",
  Y: "tyrosyl",
  V: "valyl",
};

function Display2Name({ label, acids }: { label: string; acids: string }) {
  return (
    <>
      <Tag>{label}</Tag>
      <Grid templateColumns="5em 5em 4.9em 7.2em" columnGap="3" rowGap="1">
        {acids.split("").map((acid, i) => (
          <Flex key={i} direction="column" alignItems="center">
            <Text as="pre">{ACYL_NAME[acid as keyof typeof ACYL_NAME]}</Text>
            <Text as="span" fontFamily="sans" fontSize="xs" textAlign="center">
              {AMINO_ACID_NAME[acid as keyof typeof AMINO_ACID_NAME]}
            </Text>
          </Flex>
        ))}
      </Grid>
    </>
  );
}

export function Display2() {
  const N2B = "MTTQAPTF";
  const F = "MTTQRYES";

  return (
    <Grid
      templateColumns="max-content max-content"
      columnGap="3"
      rowGap="1"
      maxWidth="full"
      overflow="auto"
      alignItems="start"
      width="fit-content"
      margin="auto"
    >
      <Display2Name label="N2-B" acids={N2B} />
      <Display2Name label="F" acids={F} />
    </Grid>
  );
}
